// Whose form is this, from the borrower's point of view.
//
// CONSUMER: shared (Workers runtime + web via @shared + tests).
//
// WHY. The hosted form pages carry one wordmark and one phone number,
// because until now every form on them was a Stewardship form sent to a
// Stewardship client. The Private Money applications are not that. A
// borrower applying to KlinkLoans Fund knows KlinkLoans; a borrower
// applying through First Tier knows Eric's lender. Handing either of them
// a page that says only "Stewardship" is not a branding preference, it is
// a page that looks like it belongs to somebody else — which on a form
// asking for a loan application is exactly when a person stops typing.
//
// private-money-loan-desk.md §8.6 asked for this as "per-template
// branding plus a second hostname". The brand follows the TEMPLATE rather
// than the hostname on purpose: a form keeps its own identity wherever it
// is served — the staging host, a preview, an embed, or the second
// hostname once DNS exists — and adding that hostname later is then a DNS
// record and one list entry, with nothing to re-test about what the page
// says.
//
// WHAT IS DELIBERATELY NOT HERE. No colours, no logo upload, no CSS. A
// brand here is a NAME, a LINE UNDER IT, and a PHONE NUMBER — the three
// things that answer "whose page am I on and who do I call". Letting a
// form carry its own stylesheet would be a small theming engine nobody
// asked for, and the one visual rule that matters (it still looks like a
// Stewardship product) is one we want to keep.
//
// Stewardship stays named on every brand. On the Private Money forms that
// is a legal fact as much as a courtesy: Stewardship Mortgage LLC is the
// Originating Agent on these loans, and the borrower should see that
// before they type, not for the first time in the acknowledgment.
//
// Pure + dependency-free.

import { OFFICE_PHONE_E164 } from "./office-contact";

export interface FormBrand {
  /** The wordmark. Two names joined by a middle dot reads correctly on a
   *  phone; anything longer wraps and looks broken. */
  wordmark: string;
  /** The line under it, when there is one. */
  tagline?: string;
  /** Digits only, E.164 without the plus — the page builds the tel: link
   *  and the display form from this. */
  phone: string;
}

export const STEWARDSHIP_BRAND: FormBrand = {
  wordmark: "Stewardship",
  phone: OFFICE_PHONE_E164,
};

/** Brands a template may declare, by key. A closed set rather than free
 *  text: a wordmark is who we are claiming to be on a page asking for
 *  someone's loan application, and that is not a field a form author
 *  should be able to type into. */
export const FORM_BRANDS: Record<string, FormBrand> = {
  stewardship: STEWARDSHIP_BRAND,
  klinkloans: {
    wordmark: "KlinkLoans Fund · Stewardship Mortgage",
    tagline: "Private money lending, originated by Stewardship Mortgage LLC.",
    phone: OFFICE_PHONE_E164,
  },
  // The long-term care prequal (2026-09-23): the mockup's header reads
  // "Stewardship Planning". The office line, because no Planning-only
  // number is on record; the drawing's number was a placeholder.
  planning: {
    wordmark: "Stewardship Planning",
    phone: OFFICE_PHONE_E164,
  },
  first_tier: {
    wordmark: "Stewardship Mortgage · Private Money",
    tagline: "Originated by Stewardship Mortgage LLC.",
    phone: OFFICE_PHONE_E164,
  },
};

/** Read a template's brand from its settings. Unknown or absent keys fall
 *  back to Stewardship, which is the right answer for every form that
 *  predates this and for a typo alike. */
export function parseFormBrand(settings: unknown): FormBrand {
  if (!settings || typeof settings !== "object") return STEWARDSHIP_BRAND;
  const key = (settings as { brand?: unknown }).brand;
  if (typeof key !== "string") return STEWARDSHIP_BRAND;
  return FORM_BRANDS[key] ?? STEWARDSHIP_BRAND;
}

export function isKnownBrandKey(key: unknown): key is string {
  return typeof key === "string" && key in FORM_BRANDS;
}

/** "(602) 384-2604" from "16023842604". Ten-digit US numbers only; any
 *  other shape is shown as given rather than mangled into a wrong one. */
export function displayPhone(e164: string): string {
  const digits = e164.replace(/\D/g, "");
  const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return e164;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export function telHref(e164: string): string {
  const digits = e164.replace(/\D/g, "");
  return `tel:+${digits}`;
}
